import { useEffect } from "react";
import AlertBanner from "../AlertBanner/AlertBanner";
import InlineSpinner from "../Spinner/InlineSpinner";

/**
 * Shared confirmation dialog with backdrop overlay.
 *
 * @param {boolean}   open            - Whether the dialog is visible.
 * @param {string}    title           - Dialog heading text.
 * @param {React.ReactNode} children  - Body content (message text, JSX, etc.).
 * @param {React.ReactNode} [icon]    - Optional centred icon element above the title.
 * @param {string}    [error]         - Optional error string shown as AlertBanner.
 * @param {string}    [cancelLabel]   - Label for the cancel button (default "Cancel").
 * @param {string}    [confirmLabel]  - Label for the confirm button (default "Confirm").
 * @param {string}    [loadingLabel]  - Label while loading (default same as confirmLabel).
 * @param {"danger"|"primary"} [confirmVariant] - Button colour variant (default "danger").
 * @param {boolean}   [isLoading]     - Shows spinner and disables buttons.
 * @param {boolean}   [showCancel]    - Whether to show the cancel button (default true).
 * @param {boolean}   [backdropClose] - Close on backdrop click (default true).
 * @param {() => void} onCancel       - Called when dialog is dismissed.
 * @param {() => void} onConfirm      - Called when confirm button is clicked.
 * @param {boolean}   [divider]       - Show an <hr> between body and buttons (default false).
 */
const ConfirmDialog = ({
  open,
  title,
  children,
  icon,
  error,
  cancelLabel = "Cancel",
  confirmLabel = "Confirm",
  loadingLabel,
  confirmVariant = "danger",
  isLoading = false,
  showCancel = true,
  backdropClose = true,
  onCancel,
  onConfirm,
  divider = false,
}) => {
  // Close on Escape, matching the convention used by AnchoredPanel,
  // GroupDetailPanel, UserDetailPanel, and MediaModal. Respect isLoading
  // the same way the cancel/confirm buttons already do (disabled while
  // a request is in flight).
  useEffect(() => {
    if (!open || isLoading) return;
    const handleKey = (e) => {
      if (e.key === "Escape") onCancel?.();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, isLoading, onCancel]);

  if (!open) return null;

  const confirmColors =
    confirmVariant === "danger"
      ? "bg-fill-red-600 hover:bg-fill-red-700 text-ink-on-color"
      : "bg-fill-blue-600 hover:bg-fill-blue-700 text-ink-on-primary";

  const activeConfirmLabel = isLoading
    ? loadingLabel || confirmLabel
    : confirmLabel;

  return (
    <div
      className="fixed inset-0 z-[1000] bg-surface-overlay/50 dark:bg-surface-overlay/70 flex items-center justify-center px-4"
      onClick={backdropClose ? onCancel : undefined}
    >
      <div
        className="relative max-w-md w-full bg-surface-dialog rounded-xl shadow-xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Optional centred icon */}
        {icon && (
          <div className="flex items-center justify-center mb-4">{icon}</div>
        )}

        {/* Title */}
        <h3 className="text-lg font-semibold text-ink-ink dark:text-ink-white mb-2 text-center">
          {title}
        </h3>

        {/* Body */}
        <div className="text-sm text-ink-gray-500 dark:text-ink-gray-400 mb-4 text-center">
          {children}
        </div>

        {/* Error banner */}
        {error && <AlertBanner className="mb-4">{error}</AlertBanner>}

        {/* Optional divider */}
        {divider && (
          <hr className="my-4 border-edge-gray-200 dark:border-edge-gray-700" />
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-3 justify-end">
          {showCancel && (
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-ink dark:hover:text-ink-white border border-edge-gray-200 dark:border-edge-panel-border rounded-lg hover:bg-fill-gray-50 dark:hover:bg-fill-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cancelLabel}
            </button>
          )}
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${confirmColors}`}
          >
            {isLoading && <InlineSpinner />}
            {activeConfirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
